import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion,AccordionSummary,Pagination,Skeleton,Stack,TextField } from '@mui/material';
import { useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEye } from 'react-icons/fa';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import QueryFn from '../../helpers/QueryFn';
import Pesquisa from '../../service/Pesquisas';
import { perguntasType,pesquisaType } from '../../types/researches/types';
import usePagination from '../Pagination/Pagination';
import ModalAlteranativas from './ModalAlteranativas';

const pesquisaService = new Pesquisa();
const queryFn = new QueryFn();

export default function ResearchesModels() {
  const { t } = useTranslation('translation');
  const [expanded, setExpanded] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [alteranativas, setAlternativas] = useState<string[]>([]);
  const [search, setSearch] = useState<string>('');
  const [paginated, setPaginated] = useState<pesquisaType[]>([]);
  const [page, setPage] = useState(1);

  const { data, isLoading, isSuccess, isError } = queryFn.useFetch('GET_RESEARCHES2',
    async () => {
      try {
        const token = await AttToken();
        if (token) {
          const response = await pesquisaService.getAll(token);
          return response;
        }
      } catch (err) {
        console.error(err);
      }
    });

  const PER_PAGE = 5;
  const count = Math.ceil(data?.length / PER_PAGE);
  const _DATA = usePagination<pesquisaType>(data?.sort((a: pesquisaType, b: pesquisaType) => { return a.dataCadastro > b.dataCadastro ? 1 : -1; }), PER_PAGE);

  useEffect(() => {
    if (isSuccess) trackEventMatomo('pesquisas legado', 'sucesso', 'carregamento', 'modelos de pesquisa carregados');
    if (isError) trackEventMatomo('pesquisas legado', 'erro', 'carregamento', 'falha ao carregar modelos de pesquisa');
  }, [isError, isSuccess]);

  useEffect(() => {
    search ? setPaginated(data
      ?.filter((e: pesquisaType) => e.nome
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(search
          ?.normalize("NFD")
          ?.replace(/[\u0300-\u036f]/g, "")
          ?.toLowerCase()))) : setPaginated(_DATA.currentData());
          }, [search, page, isLoading, data, _DATA]);

  const handleChange = (_e: React.ChangeEvent<unknown>, p: number) => {
    trackEventMatomo('pesquisas legado', 'click', 'paginação', `acessa página ${p}`);
    setPage(p);
    _DATA.jump(p);
  };

  const tipoResposta = (value: string) => {
    switch (value) {
      case "alternativa":
        return `${t('Researches.alternative')}`;
      case 'multiple':
        return `${t('Researches.multipleChoice')}`;
      case 'textoCurto':
        return `${t('Researches.shortText')}`;
      case 'textoLongo':
        return `${t('Researches.longText')}`;
      case 'horario':
        return `${t('Researches.time')}`;
      case 'data':
        return `${t('Researches.date')}`;
      case 'numero':
        return `${t('Researches.number')}`;
      case 'foto':
        return `${t('Researches.photo')}`;
      default: return '';
    }
  };

  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="flex-start"
      spacing={1}
      width={'100%'}
    >
      {isLoading
        ? <>
          <Skeleton variant="rounded" height={40} width={300} />
          <Skeleton variant='rounded' height={40} width={'100%'} />
          <Skeleton variant="rounded" height={40} width={'100%'} />
        </>
        : <>
          {data.length > 5 &&
            <>
              <Pagination
                boundaryCount={4}
                showFirstButton
                showLastButton
                count={count}
                size="small"
                page={page}
                variant='text'
                shape='circular'
                onChange={handleChange}
              />
              <TextField
                size='small'
                onClick={() => trackEventMatomo('Pesquisa legado', 'click', 'input', 'edita valor')}
                fullWidth
                label={t('Researches.search')}
                value={search}
                onChange={(event) => {
                  trackEventMatomo('pesquisas legado', 'click', 'input', 'pesquisa por nome');
                  setSearch(event.target.value.toLowerCase());
                }}
                style={{ marginBottom: '10px' }}
              />
            </>}
          {paginated?.map((modelo: pesquisaType) => (
            <Accordion style={{ width: '100%', margin: '0px 0px 10px 0px' }} expanded={expanded === modelo.id}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon onClick={() => {
                  trackEventMatomo('Pesquisa legado', 'click', 'acordeon', 'abre/fecha');
                  if (expanded === modelo.id) {
                    setExpanded('');
                  } else {
                    setExpanded(modelo.id);
                  }
                }} />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: '#18A689', fontWeight: 600, fontSize: '1.5em' }}>{modelo.nome}</span>
                  <span style={{ fontWeight: 300, fontSize: '.8em' }}>{t('Researches.createdBy')} <span style={{ fontWeight: 500 }}>{modelo.userNome}
                  </span> {t('Researches.on')} {new Date(modelo.dataCadastro).toLocaleString()}
                    &nbsp;{t('Researches.version')} {modelo.versao}</span>
                </div>
              </AccordionSummary>
              <table className='table' style={{ width: '100%' }}>
                <thead style={{ backgroundColor: '#EEEEEE' }}>
                  <tr>
                    <th>{t('Researches.questionType')}</th>
                    <th>{t('Researches.questionText')}</th>
                    <th>{t('Researches.responseType')}</th>
                  </tr>
                </thead>
                <tbody>
                  {modelo.perguntas.map((pergunta: perguntasType, index) => (
                    <tr key={index}>
                      <ModalAlteranativas alternativas={alteranativas} openModal={openModal} setOpenModal={setOpenModal} />
                      <td width={'80px'} style={{ textAlign: 'center' }}>{pergunta.id}</td>
                      <td width={'74%'}>
                        {pergunta.questao}
                      </td>
                      <th>
                        {pergunta?.alternativas
                          && <button onClick={() => {
                            trackEventMatomo('Pesquisa legado', 'click', 'button', 'abre modal');
                            setAlternativas(pergunta.alternativas);
                            setOpenModal(!openModal);
                          }} className='button is-small'><FaEye size={25} /></button>}&nbsp;
                        {tipoResposta(pergunta.tipo)}
                      </th>
                    </tr>
                  ))
                  }
                </tbody>
              </table>
            </Accordion>
          ))}
        </>}
    </Stack>

  );
}
